import { z } from "zod";

// Cross-cutting kinds apply to any tabular migration file (GL, CoA, future).
// GL-only and CoA-only kinds are subsets a given parser narrows to via the
// per-document-type profile; the wire schema accepts the full union.
export const importRejectionKindSchema = z.enum([
    "UNRECOGNIZED_LAYOUT",
    "MISSING_ROW_VALUES",
    "STRUCTURAL_SURPRISE",
    "UNMATCHED_ONE_SIDED_LINES",
    "MISSING_OPENING_BALANCES",
    "MISSING_PANDL_YEARS",
    "UNKNOWN_ACCOUNT_TYPE",
    "DUPLICATE_ACCOUNT",
    "UNMATCHED_ACCOUNT",
    "ACCOUNT_CODE_MISMATCH",
]);

export type ImportRejectionKind = z.infer<typeof importRejectionKindSchema>;

export const IMPORT_REJECTION_EXAMPLE_ROW_CAP = 3;
export const IMPORT_REJECTION_ROW_NUMBERS_CAP = 50_000;
export const IMPORT_REJECTION_HEADER_MAX_LENGTH = 100;
export const IMPORT_REJECTION_CELL_MAX_LENGTH = 500;

export const importRejectionExampleRowSchema = z.object({
    rowNumber: z.number().int().min(1),
    headers: z
        .array(z.string().max(IMPORT_REJECTION_HEADER_MAX_LENGTH))
        .max(30),
    cells: z.array(z.string().max(IMPORT_REJECTION_CELL_MAX_LENGTH)).max(30),
    problem: z.string().max(500),
});

export type ImportRejectionExampleRow = z.infer<
    typeof importRejectionExampleRowSchema
>;

export const importRejectionRowProblemSchema = z.object({
    rowNumber: z.number().int().min(1),
    problem: z.string().max(500),
});

export type ImportRejectionRowProblem = z.infer<
    typeof importRejectionRowProblemSchema
>;

// A structured reason a migration file was rejected at extract or finalize
// time: a short row status, guidance for the user, and up to a few offending
// rows so the user can find them in the source file.
export const importRejectionSchema = z.object({
    kind: importRejectionKindSchema,
    summary: z.string().max(100),
    guidance: z.string().max(500),
    // Finalize-stage rejections about the file set carry no single document.
    documentId: z.number().int().optional(),
    exampleRows: z
        .array(importRejectionExampleRowSchema)
        .max(IMPORT_REJECTION_EXAMPLE_ROW_CAP)
        .optional(),
    rowNumbers: z
        .array(z.number().int().min(1))
        .max(IMPORT_REJECTION_ROW_NUMBERS_CAP)
        .optional(),
    rowProblems: z
        .array(importRejectionRowProblemSchema)
        .max(IMPORT_REJECTION_ROW_NUMBERS_CAP)
        .optional(),
    totalCount: z.number().int().min(0).optional(),
});

export type ImportRejection = z.infer<typeof importRejectionSchema>;
